import { useMemo } from "react";
import dayjs from "dayjs";

/**
 * Helper function to subtract working days (excluding weekends).
 */
function subtractWorkingDays(date: dayjs.Dayjs, workingDays: number): string {
  let result = date.clone();
  while (workingDays > 0) {
    result = result.subtract(1, "day");
    const dayOfWeek = result.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays--;
    }
  }
  return result.format("YYYY-MM-DD");
}

/**
 * Adjusts GL Date for weekends:
 * - If GL Date is Saturday, moves it to Monday.
 * - If GL Date is Sunday, moves it to Monday.
 */
export function adjustForWeekend(date: string): string {
  const day = dayjs(date);
  const dayOfWeek = day.day();

  if (dayOfWeek === 6) {
    // Saturday → Move to Monday
    return day.add(2, "day").format("YYYY-MM-DD");
  } else if (dayOfWeek === 0) {
    // Sunday → Move to Monday
    return day.add(1, "day").format("YYYY-MM-DD");
  }

  return date; // Weekday, no adjustment needed
}

interface InvoiceData {
  glDate?: string;
}

export function useFilteredInvoiceCounts(filteredData: InvoiceData[] = []) {
  return useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { invoice_count: 0, today: 0, oneToSevenDays: 0, eightToFifteenDays: 0, sixteenPlusDays: 0, outOfSLA: 0 };
    }

    const today = dayjs().format("YYYY-MM-DD");
    const sevenDaysAgo = subtractWorkingDays(dayjs(), 7);
    const eightDaysAgo = subtractWorkingDays(dayjs(), 8);
    const fifteenDaysAgo = subtractWorkingDays(dayjs(), 15);
    const sixteenDaysAgo = subtractWorkingDays(dayjs(), 16);
    const threeDaysAgo = subtractWorkingDays(dayjs(), 3);

    let invoiceCount = filteredData.length; // Count only visible rows
    let todayCount = 0;
    let oneToSevenDaysCount = 0;
    let eightToFifteenDaysCount = 0;
    let sixteenPlusDaysCount = 0;
    let outOfSLACount = 0;

    filteredData.forEach(({ glDate }) => {
      if (!glDate) return;

      let adjustedDate = adjustForWeekend(glDate.split("T")[0]); // Adjust if it's Sat/Sun
      if (adjustedDate === today) {
        todayCount++;
      } else if (adjustedDate >= sevenDaysAgo && adjustedDate < today) {
        oneToSevenDaysCount++;
      } else if (adjustedDate >= fifteenDaysAgo && adjustedDate < eightDaysAgo) {
        eightToFifteenDaysCount++;
      } else if (adjustedDate <= sixteenDaysAgo) {
        sixteenPlusDaysCount++;
      }

      if (adjustedDate <= threeDaysAgo) {
        outOfSLACount++;
      }
    });

    return {
      invoice_count: invoiceCount,
      today: todayCount,
      oneToSevenDays: oneToSevenDaysCount,
      eightToFifteenDays: eightToFifteenDaysCount,
      sixteenPlusDays: sixteenPlusDaysCount,
      outOfSLA: outOfSLACount,
    };
  }, [filteredData]);
}
